// Ultimate Card Game (UCG) / VOID Card Warfare - Memory / crash breadcrumb probe
//
// Diagnoses the iOS "white flash, then back on the start screen" reports (iPhone, Chrome PWA).
// That symptom is WebKit killing the WebContent process under memory pressure (jetsam) and
// reloading the page. Such a kill fires no 'error', no 'unhandledrejection' and no 'pagehide',
// so js/engine/errorReporter.js never sees it. This probe leaves the evidence itself:
//
//   1. A heartbeat (~every 750 ms) persists the current screen, the pack-opening state and a
//      cheap memory estimate to localStorage, plus a short trail of the last beats.
//   2. On load the previous session record is classified:
//        clean          - 'pagehide' fired (normal reload / navigation)
//        background     - last known state was 'hidden' (iOS discards backgrounded apps; expected)
//        suspected-kill - neither: the page vanished while visible. This is the reported bug.
//      Non-clean endings are appended to a crash history together with the trail.
//   3. Per pack-opening cycle it records how DOM nodes, canvas, image and audio estimates moved
//      between open and 3 s after close, so a leak shows up as a steady climb.
//
// iOS WebKit has no performance.memory, so the numbers are ESTIMATES built from what the page can
// see: canvas pixels (w*h*4), decoded <img> pixels, and decoded Howler buffers. CSS background
// images and GPU layer textures are invisible to the page and are not counted.
//
// Recording is OFF by default: the heartbeat (style recalcs + a synchronous localStorage write every
// 750 ms, a DOM/<img> scan every 3 s) is a constant background cost on a phone. Open the game once
// with ?probe=on to switch it on (persisted in localStorage, so a home-screen PWA keeps it);
// ?probe=off switches it off again. Once recording, the overlay is hidden by default and shown on
// demand: Options > GRAPHIC & MORE > MEMORY PROBE (stored in localStorage).
//
// Purely observational; never touches gameplay state. The pure parts take their environment as
// arguments so they run in Node tests; only MemProbe.start()/the HUD touch the DOM.

import { computeFrameStats } from './archivePerf.js';
import { isDevToolsOn } from './devTools.js';

export const MEM_PROBE_STORAGE_KEY = 'ucg_probe_mem';
export const MEM_SESSION_KEY = 'ucg_memprobe_session';
export const MEM_CRASHES_KEY = 'ucg_memprobe_crashes';
export const MEM_HUD_STORAGE_KEY = 'ucg_probe_mem_hud'; // '1' = overlay shown
export const MEM_BEAT_MS = 750;
// collectMetrics() walks every DOM node and <img> and sorts the result: cheap once, but not every
// 750 ms for the whole session. Between refreshes a beat only re-stamps time/screen/pack state.
export const MEM_METRICS_MS = 3000;
export const MEM_TRAIL_LENGTH = 24;
export const MEM_MAX_CRASHES = 20;
export const MEM_MAX_CYCLES = 12;
export const MEM_CYCLE_SETTLE_MS = 3000;
export const MEM_MAX_TEARS = 12;
export const MEM_MAX_WARPS = 8;
// The first part of a Warp Speed run (flash + HUD swap + first flips) is where it can hitch; frames
// are counted separately for this long after the click, the rest is the card cascade.
export const MEM_WARP_CLICK_WINDOW_MS = 600;
// The deal-in of the pulled cards is the second place a pack opening stutters: frames are counted for
// this long after the tear ends.
export const MEM_REVEAL_WINDOW_MS = 1500;
// Frames longer than this are listed individually (offset from the tear start) so a stutter can be
// matched to a beat of the choreography, see TearFrameTracker.
export const MEM_SPIKE_MS = 50;
export const MEM_MAX_SPIKES = 14;

// Overlays whose visibility identifies "where the player is".
export const MEM_SCREEN_IDS = [
  'start-screen',
  'pack-opening-modal',
  'slot-machine-modal',
  'showroom-modal',
  'deck-modal',
  'deckbuilder-modal',
  'directives-modal',
  'online-modal',
  'campaign-intro-video-overlay',
  'game-over-modal',
];

const MB = 1024 * 1024;
const round1 = (v) => Math.round(v * 10) / 10;

// ---- flag ------------------------------------------------------------------

// Default is OFF. ?probe=on stores 'on' (opt-in, 'mem' is the legacy spelling), ?probe=off clears it again.
export function isMemProbeEnabled({ search = '', storage = null } = {}) {
  let value = null;
  try {
    value = new URLSearchParams(search).get('probe');
  } catch {
    value = null;
  }
  if (value === 'on' || value === 'mem') {
    try { storage?.setItem?.(MEM_PROBE_STORAGE_KEY, 'on'); } catch { /* ignore */ }
    return true;
  }
  if (value === 'off') {
    try { storage?.removeItem?.(MEM_PROBE_STORAGE_KEY); } catch { /* ignore */ }
    return false;
  }
  try {
    return storage?.getItem?.(MEM_PROBE_STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export function isMemHudVisible(storage = null) {
  try {
    return storage?.getItem?.(MEM_HUD_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

// ---- metrics ---------------------------------------------------------------

function visibleEl(win, el) {
  if (!el) return false;
  try {
    const cs = win.getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity || '1') > 0.02;
  } catch {
    return false;
  }
}

export function detectScreens(doc = globalThis.document, win = globalThis.window) {
  if (!doc || !win) return [];
  const out = [];
  for (const id of MEM_SCREEN_IDS) {
    const el = doc.getElementById(id);
    if (!el) continue;
    if (el.classList?.contains('hidden')) continue;
    if (visibleEl(win, el)) out.push(id);
  }
  return out;
}

// Decoded-buffer estimate for Howler's WebAudio samples. Howls that share a src share one decoded
// buffer inside Howler, so they are counted once. Assumes stereo Float32 at the context rate.
export function estimateHowlerBytes(howler) {
  const howls = howler?._howls;
  if (!Array.isArray(howls)) return { bytes: 0, count: 0 };
  const rate = howler?.ctx?.sampleRate || 44100;
  const seen = new Set();
  const items = [];
  let bytes = 0;
  let count = 0;
  for (const h of howls) {
    if (!h || !h._webAudio || !(h._duration > 0)) continue;
    const key = Array.isArray(h._src) ? h._src.join('|') : String(h._src);
    if (seen.has(key)) continue;
    seen.add(key);
    count++;
    const b = h._duration * rate * 2 * 4;
    bytes += b;
    items.push({ src: key, bytes: b });
  }
  return { bytes, count, items };
}

// Largest consumers with a shortened source, e.g. [{ src: 'assets/cards/x.png', MB: 2.8 }].
export function topConsumers(items, n = 5) {
  return items
    .slice()
    .sort((a, b) => b.bytes - a.bytes)
    .slice(0, n)
    .map((i) => {
      const src = String(i.src).replace(/^.*\/assets\//, 'assets/');
      return { src: src.length > 80 ? `…${src.slice(-79)}` : src, MB: round1(i.bytes / MB) };
    });
}

export function collectMetrics(doc = globalThis.document, win = globalThis.window) {
  const m = { dom: 0, cv: 0, cvMB: 0, imgMB: 0, imgCount: 0, audMB: 0, howls: 0, heapMB: null, estMB: 0, topImg: [], topAud: [] };
  if (!doc) return m;
  try { m.dom = doc.getElementsByTagName('*').length; } catch { /* ignore */ }
  try {
    const canvases = doc.getElementsByTagName('canvas');
    let bytes = 0;
    for (let i = 0; i < canvases.length; i++) bytes += (canvases[i].width || 0) * (canvases[i].height || 0) * 4;
    m.cv = canvases.length;
    m.cvMB = round1(bytes / MB);
  } catch { /* ignore */ }
  try {
    const imgs = doc.getElementsByTagName('img');
    let bytes = 0;
    const items = [];
    const limit = Math.min(imgs.length, 2000);
    for (let i = 0; i < limit; i++) {
      const im = imgs[i];
      if (!im.complete) continue;
      const b = (im.naturalWidth || 0) * (im.naturalHeight || 0) * 4;
      bytes += b;
      if (b > 0) items.push({ src: im.currentSrc || im.src || '?', bytes: b });
    }
    m.imgMB = round1(bytes / MB);
    m.imgCount = items.length;
    m.topImg = topConsumers(items);
  } catch { /* ignore */ }
  try {
    const { bytes, count, items } = estimateHowlerBytes(win?.Howler);
    m.audMB = round1(bytes / MB);
    m.howls = count;
    m.topAud = topConsumers(items);
  } catch { /* ignore */ }
  try {
    const used = win?.performance?.memory?.usedJSHeapSize;
    if (Number.isFinite(used)) m.heapMB = round1(used / MB);
  } catch { /* ignore */ }
  m.estMB = round1(m.cvMB + m.imgMB + m.audMB);
  return m;
}

// ---- session records --------------------------------------------------------

export function newSessionRecord({ now, id, env = {} }) {
  return {
    id,
    bootAt: now,
    lastBeatAt: now,
    exit: null,          // 'pagehide' once the page said goodbye
    visibility: 'visible',
    beat: null,          // latest compact beat
    trail: [],           // last MEM_TRAIL_LENGTH beats
    peak: { dom: 0, cvMB: 0, imgMB: 0, audMB: 0, estMB: 0 },
    cycles: [],          // pack-opening cycles, see MemProbe._finishCycle
    tears: [],           // per-tear frame timing, see TearFrameTracker
    warps: [],           // per-Warp-Speed-run frame timing, see WarpFrameTracker
    top: { img: [], aud: [] }, // largest <img> / Howler buffers at the latest beat
    env,
  };
}

export function pushTrail(rec, beat) {
  rec.beat = beat;
  rec.lastBeatAt = beat.at;
  rec.trail.push(beat);
  if (rec.trail.length > MEM_TRAIL_LENGTH) rec.trail.splice(0, rec.trail.length - MEM_TRAIL_LENGTH);
  for (const key of Object.keys(rec.peak)) {
    if ((beat[key] || 0) > rec.peak[key]) rec.peak[key] = beat[key];
  }
  return rec;
}

// 'clean' | 'background' | 'suspected-kill' | null (no previous session)
export function classifyPreviousSession(prev) {
  if (!prev || typeof prev !== 'object') return null;
  if (prev.exit === 'pagehide') return 'clean';
  if (prev.visibility === 'hidden') return 'background';
  return 'suspected-kill';
}

export function buildCrashEntry(prev, kind, now) {
  const last = prev.beat || {};
  return {
    kind,
    detectedAt: now,
    bootAt: prev.bootAt,
    lastBeatAt: prev.lastBeatAt,
    uptimeS: Math.round(((prev.lastBeatAt || prev.bootAt) - prev.bootAt) / 1000),
    screen: last.scr || '?',
    pack: last.pack || '-',
    last,
    peak: prev.peak,
    top: prev.top || null,
    cycles: (prev.cycles || []).slice(-4),
    tears: (prev.tears || []).slice(-4),
    warps: (prev.warps || []).slice(-4),
    trail: (prev.trail || []).slice(-10),
    env: prev.env,
  };
}

export function appendCrash(list, entry) {
  const out = Array.isArray(list) ? list.slice() : [];
  out.push(entry);
  if (out.length > MEM_MAX_CRASHES) out.splice(0, out.length - MEM_MAX_CRASHES);
  return out;
}

export function describeCrash(c) {
  if (!c) return '';
  const at = c.lastBeatAt ? new Date(c.lastBeatAt).toLocaleTimeString() : '?';
  const l = c.last || {};
  return `${c.kind.toUpperCase()} @${at} up ${c.uptimeS}s screen=${c.screen} pack=${c.pack} `
    + `dom=${l.dom ?? '?'} cv=${l.cv ?? '?'}/${l.cvMB ?? '?'}MB img=${l.imgMB ?? '?'}MB aud=${l.audMB ?? '?'}MB est=${l.estMB ?? '?'}MB`;
}

export function describeCycle(c) {
  return `#${c.n} ${c.durS}s  dom ${signed(c.dDom)}  cv ${signed(c.dCvMB)}MB  img ${signed(c.dImgMB)}MB  aud ${signed(c.dAudMB)}MB  est ${signed(c.dEstMB)}MB`;
}

function signed(v) {
  const r = round1(v);
  return r > 0 ? `+${r}` : `${r}`;
}

// ---- pack-tear frame timing -------------------------------------------------

// The probe's memory numbers say nothing about "the second pack opening stutters and the phone gets
// warm". This turns the gaps between animation frames into one record per tear: how the tear itself
// (pack state 'tearing') and the card deal-in right after it (MEM_REVEAL_WINDOW_MS) ran. Comparing tear
// 1 with tear 2 shows whether the device throttled (everything slower) or something specific hitched
// (one huge gap). Pure: push() is fed (pack state, timestamp, gap) by MemProbe's rAF loop.
export class TearFrameTracker {
  constructor(onTear = () => {}) {
    this.onTear = onTear;
    this.count = 0;
    this.cur = null;
  }

  static _stats(deltas) {
    const s = computeFrameStats(deltas);
    return { frames: s.frames, p50: s.p50, p95: s.p95, max: s.max, over33: s.over33, over50: s.over50, over100: s.over100 };
  }

  _finish(now) {
    const c = this.cur;
    this.cur = null;
    if (!c || c.tear.length === 0) return;
    this.onTear({
      n: ++this.count,
      at: c.startedAt,
      tearMs: Math.round(c.tearEndedAt - c.startedAt),
      tear: TearFrameTracker._stats(c.tear),
      reveal: TearFrameTracker._stats(c.reveal),
      // [ms since the tear started, frame length ms, 't' tear | 'r' deal-in], in time order
      spikes: c.spikes.sort((a, b) => a[0] - b[0]),
    });
  }

  _spike(c, now, gap, phase) {
    if (gap <= MEM_SPIKE_MS) return;
    c.spikes.push([Math.round(now - c.startedAt), Math.round(gap), phase]);
    if (c.spikes.length > MEM_MAX_SPIKES) {
      let min = 0;
      for (let i = 1; i < c.spikes.length; i++) if (c.spikes[i][1] < c.spikes[min][1]) min = i;
      c.spikes.splice(min, 1); // keep the worst ones
    }
  }

  // gap = ms since the previous frame; gaps over 1 s are a backgrounded page, not jank.
  push(pack, now, gap) {
    const ok = Number.isFinite(gap) && gap > 0 && gap < 1000;
    if (pack === 'tearing') {
      if (!this.cur) this.cur = { startedAt: now, tearEndedAt: now, revealStart: null, tear: [], reveal: [], spikes: [] };
      if (ok) { this.cur.tear.push(gap); this._spike(this.cur, now, gap, 't'); }
      this.cur.tearEndedAt = now;
      return;
    }
    const c = this.cur;
    if (!c) return;
    if (pack === 'opened') {
      if (c.revealStart === null) c.revealStart = now;
      if (now - c.revealStart <= MEM_REVEAL_WINDOW_MS) {
        if (ok) { c.reveal.push(gap); this._spike(c, now, gap, 'r'); }
        return;
      }
    }
    this._finish(now); // window over, or the modal went back to idle / closed
  }
}

// Warp Speed (the accelerated Reveal All): one record per run, the click window (flash, HUD swap, first
// flips) apart from the cascade that follows. Fed (warp running?, timestamp, gap) by MemProbe's rAF loop,
// so the 'nowarpfx' / 'nowarpaudio' A/B flags (stamped via ctx.ab) can be compared run against run.
export class WarpFrameTracker {
  constructor(onWarp = () => {}) {
    this.onWarp = onWarp;
    this.count = 0;
    this.cur = null;
  }

  _finish() {
    const c = this.cur;
    this.cur = null;
    if (!c || c.click.length + c.cascade.length === 0) return;
    this.onWarp({
      n: ++this.count,
      at: c.startedAt,
      ms: Math.round(c.endedAt - c.startedAt),
      click: TearFrameTracker._stats(c.click),
      cascade: TearFrameTracker._stats(c.cascade),
      // [ms since the click, frame length ms], time order
      spikes: c.spikes.sort((a, b) => a[0] - b[0]),
    });
  }

  push(warping, now, gap) {
    if (!warping) { this._finish(); return; }
    if (!this.cur) this.cur = { startedAt: now, endedAt: now, click: [], cascade: [], spikes: [] };
    const c = this.cur;
    c.endedAt = now;
    if (!(Number.isFinite(gap) && gap > 0 && gap < 1000)) return;
    (now - c.startedAt <= MEM_WARP_CLICK_WINDOW_MS ? c.click : c.cascade).push(gap);
    if (gap > MEM_SPIKE_MS) {
      c.spikes.push([Math.round(now - c.startedAt), Math.round(gap)]);
      if (c.spikes.length > MEM_MAX_SPIKES) {
        let min = 0;
        for (let i = 1; i < c.spikes.length; i++) if (c.spikes[i][1] < c.spikes[min][1]) min = i;
        c.spikes.splice(min, 1);
      }
    }
  }
}

// ---- probe ------------------------------------------------------------------

export class MemProbe {
  constructor({ doc, win, storage, now = () => Date.now(), getContext = null } = {}) {
    this.doc = doc || globalThis.document;
    this.win = win || globalThis.window;
    this.storage = storage ?? null;
    this._now = now;
    this._getContext = getContext;
    this.record = null;
    this.previousKind = null;
    this.crashes = [];
    this.lastMetrics = null;
    this._metricsAt = 0;
    this._running = false;
    this._lastRunningAt = 0;
    this._framesOn = false;
    this._lastFrameT = null;
    this._tears = new TearFrameTracker((t) => this._recordTear(t));
    this._warps = new WarpFrameTracker((w) => this._recordWarp(w));
    this._lastPackOpen = false;
    this._timer = null;
    this._cycleOpen = null;
    this._cyclePending = null;
    this._cycleCount = 0;
    this.onBeat = null;
  }

  setContextProvider(fn) {
    this._getContext = fn;
  }

  _read(key) {
    try {
      const raw = this.storage?.getItem?.(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  _write(key, value) {
    try {
      this.storage?.setItem?.(key, JSON.stringify(value));
    } catch { /* quota / private mode: the probe must never break the game */ }
  }

  start() {
    const now = this._now();
    const prev = this._read(MEM_SESSION_KEY);
    this.crashes = this._read(MEM_CRASHES_KEY) || [];
    this.previousKind = classifyPreviousSession(prev);
    if (this.previousKind && this.previousKind !== 'clean') {
      this.crashes = appendCrash(this.crashes, buildCrashEntry(prev, this.previousKind, now));
      this._write(MEM_CRASHES_KEY, this.crashes);
    }

    const nav = this.win?.navigator || {};
    this.record = newSessionRecord({
      now,
      id: now.toString(36),
      env: {
        ua: String(nav.userAgent || '').slice(0, 160),
        dpr: this.win?.devicePixelRatio || 1,
        vw: this.win?.innerWidth || 0,
        vh: this.win?.innerHeight || 0,
        pwa: !!this.doc?.documentElement?.classList?.contains('is-pwa'),
      },
    });
    this._write(MEM_SESSION_KEY, this.record);

    const win = this.win;
    win?.addEventListener?.('pagehide', () => this._onPageHide());
    win?.addEventListener?.('pageshow', () => this._onPageShow());
    this.doc?.addEventListener?.('visibilitychange', () => this._onVisibility());
    this._timer = win?.setInterval?.(() => this.beat(), MEM_BEAT_MS) ?? null;
    this.beat();
    return this;
  }

  _onPageHide() {
    if (!this.record) return;
    this.record.exit = 'pagehide';
    this._write(MEM_SESSION_KEY, this.record);
  }

  // Restored from the back/forward cache: the session goes on, so it must not read as "ended".
  _onPageShow() {
    if (!this.record) return;
    this.record.exit = null;
    this.record.visibility = this.doc?.visibilityState === 'hidden' ? 'hidden' : 'visible';
    this._write(MEM_SESSION_KEY, this.record);
  }

  _onVisibility() {
    if (!this.record) return;
    this.record.visibility = this.doc?.visibilityState === 'hidden' ? 'hidden' : 'visible';
    this._write(MEM_SESSION_KEY, this.record);
  }

  beat() {
    if (!this.record) return null;
    const now = this._now();
    let ctx = {};
    try { ctx = this._getContext?.() || {}; } catch { ctx = {}; }
    // Fresh numbers are needed when the pack opens/closes and when a cycle is due to be closed out
    // (its deltas are exactly "metrics at open" vs "metrics after close"). During the tear itself
    // the scan is skipped: it is the most animation-critical stretch of the whole game.
    const packOpen = !!ctx.packOpen;
    const running = !!ctx.running;
    this._running = running;
    if (running) this._lastRunningAt = now;
    this._syncFrameLoop(packOpen);
    const cycleDue = this._isCycleDue(now);
    const force = !this.lastMetrics || cycleDue || packOpen !== this._lastPackOpen;
    const skip = !force && (ctx.pack === 'tearing' || now - this._metricsAt < MEM_METRICS_MS);
    this._lastPackOpen = packOpen;
    if (!skip) this._metricsAt = now;
    const m = skip ? this.lastMetrics : collectMetrics(this.doc, this.win);
    const screens = detectScreens(this.doc, this.win);
    const beat = {
      at: now,
      t: Math.round((now - this.record.bootAt) / 1000),
      scr: screens.join('+') || 'none',
      pack: ctx.pack || '-',
      run: ctx.running ? 1 : 0,
      aud: ctx.audioState || '-',
      dom: m.dom,
      cv: m.cv,
      cvMB: m.cvMB,
      imgMB: m.imgMB,
      audMB: m.audMB,
      estMB: m.estMB,
    };
    if (m.heapMB !== null) beat.heapMB = m.heapMB;
    this.lastMetrics = m;
    this.record.top = { img: m.topImg, aud: m.topAud, imgCount: m.imgCount, howls: m.howls };
    this._trackCycle(packOpen, m, now, cycleDue);
    pushTrail(this.record, beat);
    this._write(MEM_SESSION_KEY, this.record);
    try { this.onBeat?.(beat); } catch { /* HUD must not break the beat */ }
    return beat;
  }

  // rAF gaps only while the pack opening is open (nothing runs during matches or other menus). One
  // callback per frame that pushes a number; the pack state is read from the same context provider the
  // beats use.
  _syncFrameLoop(packOpen) {
    const raf = this.win?.requestAnimationFrame;
    if (typeof raf !== 'function') return;
    if (packOpen && !this._framesOn) {
      this._framesOn = true;
      this._lastFrameT = null;
      raf.call(this.win, this._frame);
    } else if (!packOpen && this._framesOn) {
      this._framesOn = false;
      this._tears.push('idle', this._now(), 0); // closes a tear that was still being counted
      this._warps.push(false, this._now(), 0);
    }
  }

  _frame = (t) => {
    if (!this._framesOn) return;
    this.win.requestAnimationFrame(this._frame);
    const prev = this._lastFrameT;
    this._lastFrameT = t;
    if (prev === null) return;
    let pack;
    let warp = false;
    try {
      const ctx = this._getContext?.() || {};
      pack = ctx.pack;
      warp = !!ctx.warp;
    } catch { pack = undefined; }
    this._tears.push(pack, t, t - prev);
    this._warps.push(warp, t, t - prev);
  };

  _recordWarp(warp) {
    if (!this.record) return;
    let ab;
    try { ab = this._getContext?.().ab; } catch { ab = undefined; }
    if (Array.isArray(ab) && ab.length) warp.ab = ab;
    (this.record.warps ||= []).push(warp);
    if (this.record.warps.length > MEM_MAX_WARPS) this.record.warps.shift();
  }

  _recordTear(tear) {
    if (!this.record) return;
    // Which developer A/B switches were on (js/ui/abFlags.js); absent = the default configuration.
    let ab;
    try { ab = this._getContext?.().ab; } catch { ab = undefined; }
    if (Array.isArray(ab) && ab.length) tear.ab = ab;
    this.record.tears.push(tear);
    if (this.record.tears.length > MEM_MAX_TEARS) this.record.tears.shift();
  }

  // Pack-opening cycle: metrics at open vs 3 s after close. "After close" means the player is back
  // in the menus: a match started straight from the Cargo Bay loads ~90 MB of ambience loops that
  // say nothing about the pack, so the cycle waits until 3 s after the match is over as well.
  _isCycleDue(now) {
    const p = this._cyclePending;
    if (!p || this._running) return false;
    return now - Math.max(p.closedAt, this._lastRunningAt || 0) >= MEM_CYCLE_SETTLE_MS;
  }

  _trackCycle(packOpen, m, now, due = this._isCycleDue(now)) {
    if (packOpen && !this._cycleOpen) {
      this._cycleOpen = { at: now, m };
      this._cyclePending = null;
    } else if (!packOpen && this._cycleOpen) {
      this._cyclePending = { ...this._cycleOpen, closedAt: now };
      this._cycleOpen = null;
    }
    if (due && this._cyclePending) {
      this._finishCycle(this._cyclePending, m);
      this._cyclePending = null;
    }
  }

  _finishCycle(open, m) {
    const c = {
      n: ++this._cycleCount,
      durS: Math.round((open.closedAt - open.at) / 1000),
      dDom: m.dom - open.m.dom,
      dCvMB: round1(m.cvMB - open.m.cvMB),
      dImgMB: round1(m.imgMB - open.m.imgMB),
      dAudMB: round1(m.audMB - open.m.audMB),
      dEstMB: round1(m.estMB - open.m.estMB),
    };
    this.record.cycles.push(c);
    if (this.record.cycles.length > MEM_MAX_CYCLES) this.record.cycles.shift();
  }

  clearHistory() {
    this.crashes = [];
    try { this.storage?.removeItem?.(MEM_CRASHES_KEY); } catch { /* ignore */ }
  }

  hudVisible() {
    return !!this.doc?.getElementById?.('mem-probe-hud');
  }

  // Shows/hides the overlay (persisted). The recording is unaffected either way.
  setHudVisible(on) {
    try {
      if (on) this.storage?.setItem?.(MEM_HUD_STORAGE_KEY, '1');
      else this.storage?.removeItem?.(MEM_HUD_STORAGE_KEY);
    } catch { /* ignore */ }
    if (on) mountMemHud(this, this.doc);
    else unmountMemHud(this, this.doc);
    return this.hudVisible();
  }

  exportText() {
    return JSON.stringify({
      previous: this.previousKind,
      crashes: this.crashes,
      session: this.record,
    }, null, 1);
  }
}

// ---- HUD (the only DOM-styling part) ----------------------------------------

export function mountMemHud(probe, doc = globalThis.document) {
  if (!doc || !doc.body || doc.getElementById('mem-probe-hud')) return null;
  const hud = doc.createElement('div');
  hud.id = 'mem-probe-hud';
  hud.style.cssText = [
    'position:fixed', 'left:4px', 'top:calc(env(safe-area-inset-top, 0px) + 4px)', 'z-index:2147483000',
    'max-width:min(96vw,560px)', 'max-height:60vh', 'overflow:auto', 'padding:3px 6px', 'border-radius:6px',
    'background:rgba(0,0,0,.72)', 'border:1px solid rgba(0,240,255,.4)', 'color:#9fe',
    'font:10px/1.35 ui-monospace,Menlo,monospace', 'white-space:pre-wrap', 'pointer-events:auto',
    '-webkit-tap-highlight-color:transparent',
  ].join(';');

  const line = doc.createElement('div');
  const detail = doc.createElement('div');
  const buttons = doc.createElement('div');
  detail.style.display = 'none';
  buttons.style.cssText = 'display:none;gap:4px;margin-top:4px;flex-wrap:wrap';
  hud.append(line, detail, buttons);

  const badge = () => {
    const bad = probe.crashes.filter((c) => c.kind === 'suspected-kill').length;
    const bg = probe.crashes.filter((c) => c.kind === 'background').length;
    return `kills:${bad} bg:${bg}`;
  };

  const renderLine = (beat) => {
    if (!beat) return;
    const heap = beat.heapMB !== undefined ? ` heap${beat.heapMB}` : '';
    line.textContent = `MEM ${beat.scr}${beat.pack !== '-' ? '/' + beat.pack : ''} dom${beat.dom} cv${beat.cv}:${beat.cvMB}M img${beat.imgMB}M aud${beat.audMB}M est${beat.estMB}M${heap} ${badge()}`;
    line.style.color = probe.previousKind === 'suspected-kill' ? '#ff8a8a' : '#9fe';
  };

  const renderDetail = () => {
    const rows = [];
    rows.push(`previous session: ${probe.previousKind || 'none'}`);
    const last = probe.crashes.slice(-4).reverse();
    rows.push(last.length ? '--- last non-clean endings ---' : 'no non-clean endings recorded');
    for (const c of last) rows.push(describeCrash(c));
    const cycles = probe.record?.cycles || [];
    if (cycles.length) {
      rows.push('--- pack cycles (open -> +3s after close) ---');
      for (const c of cycles.slice(-6)) rows.push(describeCycle(c));
    }
    const top = probe.record?.top;
    if (top?.img?.length) rows.push(`top img (${top.imgCount} decoded): ` + top.img.map((t) => `${t.MB}M ${t.src}`).join(' | '));
    if (top?.aud?.length) rows.push(`top audio (${top.howls} buffers): ` + top.aud.map((t) => `${t.MB}M ${t.src}`).join(' | '));
    const p = probe.record?.peak;
    if (p) rows.push(`peak this session: dom ${p.dom} cv ${p.cvMB}M img ${p.imgMB}M aud ${p.audMB}M est ${p.estMB}M`);
    detail.textContent = rows.join('\n');
  };

  const mkBtn = (text, onClick) => {
    const b = doc.createElement('button');
    b.type = 'button';
    b.textContent = text;
    b.style.cssText = 'font:inherit;padding:3px 6px;border-radius:4px;border:1px solid rgba(0,240,255,.5);background:rgba(15,23,42,.9);color:#cff;touch-action:manipulation';
    b.addEventListener('click', (e) => { e.stopPropagation(); onClick(b); });
    buttons.appendChild(b);
  };
  mkBtn('COPY', (b) => {
    try { globalThis.navigator?.clipboard?.writeText?.(probe.exportText()); b.textContent = 'copied'; } catch { b.textContent = 'copy failed'; }
    setTimeout(() => { b.textContent = 'COPY'; }, 1500);
  });
  mkBtn('CLEAR', () => { probe.clearHistory(); renderDetail(); renderLine(probe.record?.beat); });
  mkBtn('HIDE', () => { probe.setHudVisible(false); });

  let expanded = false;
  line.addEventListener('click', () => {
    expanded = !expanded;
    detail.style.display = expanded ? 'block' : 'none';
    buttons.style.display = expanded ? 'flex' : 'none';
    if (expanded) renderDetail();
  });
  probe.onBeat = (beat) => {
    renderLine(beat);
    if (expanded) renderDetail();
  };

  renderLine(probe.record?.beat);
  doc.body.appendChild(hud);
  return hud;
}

export function unmountMemHud(probe, doc = globalThis.document) {
  probe.onBeat = null;
  doc?.getElementById?.('mem-probe-hud')?.remove();
}

// ---- entry point ------------------------------------------------------------

// Idempotent: returns the running probe (or null when switched off / no DOM).
export function startMemProbe({ getContext = null } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (window.__memProbe) {
    if (getContext) window.__memProbe.setContextProvider(getContext);
    return window.__memProbe;
  }
  let storage = null;
  try { storage = window.localStorage; } catch { storage = null; }
  if (!isMemProbeEnabled({ search: window.location?.search || '', storage })) return null;
  const probe = new MemProbe({ doc: document, win: window, storage, getContext });
  probe.start();
  window.__memProbe = probe;
  // The overlay is a developer tool: it only comes back after the hidden Options gesture (js/ui/devTools.js),
  // even if it was left switched on.
  const mount = () => { if (isMemHudVisible(storage) && isDevToolsOn(storage)) mountMemHud(probe, document); };
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });
  return probe;
}
